import { subDays } from "date-fns/subDays";
import { subMonths } from "date-fns/subMonths";
import { formatInTimeZone, fromZonedTime, toZonedTime } from "date-fns-tz";
import { storeToRefs } from "pinia";
import type { Ref } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import {
  dateToTimestamp,
  endOfDateInTimezone,
  formatIntlDate,
  getTimezone,
  parseDate,
  startOfDateInTimezone,
  subtractTime,
} from "shared/helpers/date";
import type { AppConfig } from "shared/services/appConfig/types";
import { useGlobalStore } from "shared/stores/global";
import { useUserStore } from "shared/stores/user";

interface DateRangeRangeOption {
  daysBack?: number;
  fromContentDateCap?: boolean;
  fromNow?: boolean;
  includeToday?: boolean;
  label: string;
  localeKey: string;
  localeLabel?: string;
  monthsBack?: number;
  quartersBack?: number;
}

interface DateRangeRangeOptions {
  [key: string]: DateRangeRangeOption;
}

interface DateRangeParams {
  after?: number;
  before?: number;
  range?: DateRangeRangeOption | null;
}

interface DateRangeSpanIntervals {
  days: number;
  hours: number;
  minutes: number;
  months: number;
  quarters: number;
  weeks: number;
  years: number;
}

const rangeOptions: DateRangeRangeOptions = {
  contentDateCap: {
    fromContentDateCap: true,
    label: "Content Start Date",
    localeKey: "date.ranges.content_date_cap",
  },
  currentMonth: {
    label: "This Month",
    localeKey: "date.ranges.this_month",
    monthsBack: 0,
  },
  fourteenDays: {
    daysBack: 14,
    includeToday: true,
    label: "Last 14 Days",
    localeKey: "date.ranges.last_14_days",
  },
  halfYearly: {
    includeToday: true,
    label: "Half Yearly",
    localeKey: "date.ranges.half_yearly",
    quartersBack: 2,
  },
  last24Hours: {
    daysBack: 1,
    fromNow: true,
    label: "Last 24 Hours",
    localeKey: "date.ranges.last_24_hours",
  },
  lastMonth: {
    label: "Last Month",
    localeKey: "date.ranges.last_month",
    monthsBack: 1,
  },
  lastQuarter: {
    includeToday: true,
    label: "Last Quarter",
    localeKey: "date.ranges.last_quarter",
    quartersBack: 1,
  },
  lastYear: {
    daysBack: 365,
    includeToday: true,
    label: "Last 365 Days",
    localeKey: "date.ranges.last_year",
  },
  ninetyDays: {
    daysBack: 90,
    includeToday: true,
    label: "Last 90 Days",
    localeKey: "date.ranges.last_90_days",
  },
  oneHundredEightyDays: {
    daysBack: 180,
    includeToday: true,
    label: "Last 6 Months",
    localeKey: "date.ranges.last_180_days",
  },
  sevenDays: {
    daysBack: 7,
    includeToday: true,
    label: "Last 7 Days",
    localeKey: "date.ranges.last_7_days",
  },
  thirtyDays: {
    daysBack: 30,
    includeToday: true,
    label: "Last 30 Days",
    localeKey: "date.ranges.last_30_days",
  },
  threeDays: {
    daysBack: 3,
    includeToday: true,
    label: "Last 3 Days",
    localeKey: "date.ranges.last_3_days",
  },
  today: {
    daysBack: 1,
    includeToday: true,
    label: "Today",
    localeKey: "date.ranges.today",
  },
  yesterday: {
    daysBack: 2,
    includeToday: false,
    label: "Yesterday",
    localeKey: "date.ranges.yesterday",
  },
};

const ONE_HOUR = 3600;
const ONE_DAY = 86400;
const ONE_YEAR = ONE_DAY * 365;

function asDateString(timestamp: number, format = "dd MMM yyyy HH:mm"): string {
  return formatInTimeZone(parseDate(timestamp), getUserTimezone(), format);
}

function asTimestamp(dateObject: Date): number {
  return dateToTimestamp(dateObject);
}

function getUserTimezone(): string {
  try {
    const { currentUser } = storeToRefs(useUserStore());

    if (currentUser.value?.time_zone) {
      return currentUser.value.time_zone;
    }
  } catch {
    // Pinia may not be initialized yet (admin app's global store calls DateRange.lastSevenDays() at module level)
  }

  return getTimezone();
}

/**
 * This class is designed to represent a date range.
 */
export default class DateRange {
  after: number;

  before: number;

  range: DateRangeRangeOption | null;

  get interval(): string {
    return this.span <= 24 * ONE_HOUR ? "1h" : "1d";
  }

  get localRangeLabel(): string {
    return this.range?.localeLabel || "";
  }

  get prettyRangeText(): string {
    const label = this.range?.label;
    const localeLabel = this.range?.localeLabel;

    switch (label) {
      case "Today":
      case "Yesterday":
        return getLocaleText("date.pretty_ranges.today_yesterday");
      case "Last 3 Days":
      case "Last 7 Days":
      case "Last 14 Days":
      case "Last 30 Days":
      case "Last 90 Days":
      case "Last 180 Days":
        return localeLabel!.toLowerCase();
      case "This Month": {
        const days = Math.round(this.span / ONE_DAY);

        return getLocaleText("date.pretty_ranges.this_month", { days });
      }
      case "Last Month":
        return getLocaleText("date.pretty_ranges.last_month");
      case "Last 365 Days":
        return getLocaleText("date.pretty_ranges.last_year");
      case "Last Quarter":
        return getLocaleText("date.pretty_ranges.last_quarter");
      default:
        return getLocaleText("date.pretty_ranges.default", {
          rangeText: this.rangeText,
        });
    }
  }

  get rangeLabel(): string {
    return this.range?.label || "";
  }

  get rangeText(): string {
    const { span } = this;

    if (span < ONE_HOUR)
      return getLocaleText("date.pretty_ranges.last_minutes", {
        span: Math.round(span / 60),
      });
    if (span < ONE_DAY)
      return getLocaleText("date.pretty_ranges.last_hours", {
        span: Math.round(span / ONE_HOUR),
      });
    if (span === ONE_DAY) return getLocaleText("date.pretty_ranges.last_day");

    if (span > ONE_YEAR) {
      return getLocaleText("date.pretty_ranges.last_years", {
        span: Math.round(span / ONE_YEAR),
      });
    }

    return getLocaleText("date.pretty_ranges.last_days", {
      span: Math.round(span / ONE_DAY),
    });
  }

  get span(): number {
    return this.before - this.after;
  }

  get spanIntervals(): DateRangeSpanIntervals {
    return {
      days: Math.ceil(this.span / (24 * ONE_HOUR)),
      hours: Math.ceil(this.span / ONE_HOUR),
      minutes: Math.ceil(this.span / 60),
      months: Math.floor(this.span / (30 * 24 * ONE_HOUR)),
      quarters: Math.floor(this.span / (90 * 24 * ONE_HOUR)),
      weeks: Math.floor(this.span / (7 * 24 * ONE_HOUR)),
      years: Math.round(this.span / (365 * 24 * ONE_HOUR)),
    };
  }

  constructor({ after, before, range = null }: DateRangeParams = {}) {
    this.before = before!;
    this.after = after!;

    if (range) {
      if (range.localeKey) {
        Object.assign(range, {
          localeLabel: getLocaleText(range.localeKey),
        });
      }

      this.range = range;

      this.calculateRange();
    }
  }

  static clone(dateRange: DateRange): DateRange {
    return new DateRange(dateRange.attributes());
  }

  static contentDateCap(): DateRange {
    return new DateRange({ range: rangeOptions.contentDateCap });
  }

  static currentMonth(): DateRange {
    return new DateRange({ range: rangeOptions.currentMonth });
  }

  static fromDates(after: Date, before: Date): DateRange {
    return new DateRange({
      after: asTimestamp(after),
      before: asTimestamp(before),
    });
  }

  static halfYearly(): DateRange {
    return new DateRange({ range: rangeOptions.halfYearly });
  }

  static last24Hours(): DateRange {
    return new DateRange({ range: rangeOptions.last24Hours });
  }

  static lastFourteenDays(): DateRange {
    return new DateRange({ range: rangeOptions.fourteenDays });
  }

  static lastMonth(): DateRange {
    return new DateRange({ range: rangeOptions.lastMonth });
  }

  static lastNinetyDays(): DateRange {
    return new DateRange({ range: rangeOptions.ninetyDays });
  }

  static lastOneHundredEightyDays(): DateRange {
    return new DateRange({ range: rangeOptions.oneHundredEightyDays });
  }

  static lastQuarter(): DateRange {
    return new DateRange({ range: rangeOptions.lastQuarter });
  }

  static lastSevenDays(): DateRange {
    return new DateRange({ range: rangeOptions.sevenDays });
  }

  static lastThirtyDays(): DateRange {
    return new DateRange({ range: rangeOptions.thirtyDays });
  }

  static lastThreeDays(): DateRange {
    return new DateRange({ range: rangeOptions.threeDays });
  }

  static lastYear(): DateRange {
    return new DateRange({ range: rangeOptions.lastYear });
  }

  static rangeForLabel(label: string): DateRange {
    switch (label) {
      case "Today":
        return DateRange.today();
      case "Yesterday":
        return DateRange.yesterday();
      case "Last 3 Days":
        return DateRange.lastThreeDays();
      case "Last 7 Days":
        return DateRange.lastSevenDays();
      case "Last 14 Days":
        return DateRange.lastFourteenDays();
      case "Last 30 Days":
        return DateRange.lastThirtyDays();
      case "Last 90 Days":
        return DateRange.lastNinetyDays();
      case "Last 180 Days":
        return DateRange.lastOneHundredEightyDays();
      case "This Month":
        return DateRange.currentMonth();
      case "Last Month":
        return DateRange.lastMonth();
      case "Last Quarter":
        return DateRange.lastQuarter();
      case "Half Yearly":
        return DateRange.halfYearly();
      case "Last 365 Days":
        return DateRange.lastYear();
      case "Last 24 Hours":
        return DateRange.last24Hours();
      default:
        return DateRange.today();
    }
  }

  static today(): DateRange {
    return new DateRange({ range: rangeOptions.today });
  }

  static yesterday(): DateRange {
    return new DateRange({ range: rangeOptions.yesterday });
  }

  afterAsString(format = "dd MMM yyyy HH:mm"): string {
    return asDateString(this.after, format);
  }

  attributes(): DateRangeParams {
    return {
      after: this.after,
      before: this.before,
      range: this.range,
    };
  }

  beforeAsString(format = "dd MMM yyyy HH:mm"): string {
    return asDateString(this.before, format);
  }

  calculateQuarters(quartersBack: number, timezone: string): DateRangeParams {
    const nowInTimezone = toZonedTime(new Date(), timezone);

    const afterDate = new Date(nowInTimezone.getTime());
    afterDate.setMonth(afterDate.getMonth() - 3 * quartersBack);

    const quarterStartMonth = Math.floor(afterDate.getMonth() / 3) * 3;
    afterDate.setMonth(quarterStartMonth);
    afterDate.setDate(1);
    afterDate.setHours(0, 0, 0, 0);

    const beforeDate = new Date(nowInTimezone.getTime());

    let quarterEndMonth;

    if (beforeDate.getMonth() <= 2) {
      quarterEndMonth = 12;
    } else if (beforeDate.getMonth() <= 5) {
      quarterEndMonth = 3;
    } else if (beforeDate.getMonth() <= 8) {
      quarterEndMonth = 6;
    } else {
      quarterEndMonth = 9;
    }

    if (quartersBack === 1) {
      beforeDate.setFullYear(afterDate.getFullYear());
    }

    beforeDate.setMonth(quarterEndMonth);
    beforeDate.setDate(0);
    beforeDate.setHours(23, 59, 59, 999);

    return {
      after: asTimestamp(fromZonedTime(afterDate, timezone)),
      before: asTimestamp(fromZonedTime(beforeDate, timezone)),
    };
  }

  calculateRange(): void {
    const timezone = getUserTimezone();

    if (this.range!.monthsBack !== undefined) {
      const now = new Date();
      const endOfCurrentDay = endOfDateInTimezone(now, "day", timezone);

      const startOfMonth = startOfDateInTimezone(now, "month", timezone);

      const after = fromZonedTime(
        subMonths(toZonedTime(startOfMonth, timezone), this.range!.monthsBack),
        timezone
      );

      let before = endOfDateInTimezone(after, "month", timezone);

      if (before > endOfCurrentDay) before = endOfCurrentDay;

      this.after = asTimestamp(after);
      this.before = asTimestamp(before);
    } else if (this.range!.quartersBack !== undefined) {
      const { after, before } = this.calculateQuarters(
        this.range!.quartersBack,
        timezone
      );

      this.before = before!;
      this.after = after!;
    } else if (this.range!.fromNow) {
      const now = new Date();
      this.before = asTimestamp(now);
      this.after = asTimestamp(subtractTime(now, this.range!.daysBack!, "day"));
    } else if (this.range!.fromContentDateCap) {
      const appConfig = storeToRefs(useGlobalStore())
        .appConfig as Ref<AppConfig>;

      this.before = asTimestamp(
        endOfDateInTimezone(new Date(), "day", timezone)
      );

      this.after = appConfig.value.contentDateCap;
    } else {
      const now = new Date();
      const startOfToday = startOfDateInTimezone(now, "day", timezone);

      this.after = asTimestamp(
        fromZonedTime(
          subDays(
            toZonedTime(startOfToday, timezone),
            this.range!.daysBack! - 1
          ),
          timezone
        )
      );

      this.before = asTimestamp(
        this.range!.includeToday
          ? endOfDateInTimezone(now, "day", timezone)
          : startOfToday
      );
    }
  }

  equals(dateRange: DateRange | unknown): boolean {
    if (!(dateRange instanceof DateRange)) return false;

    return (
      JSON.stringify(this.attributes()) ===
      JSON.stringify(dateRange.attributes())
    );
  }

  justifyRange(interval: string): void {
    const { span } = this;
    const intervalType = interval[interval.length - 1];
    const intervalSize = parseInt(interval.slice(0, interval.length - 1), 10);

    const intervalSeconds: { [key: string]: number } = {
      d: 86400,
      h: 3600,
      m: 60,
      s: 1,
    };

    const secondsPerInterval = intervalSize * intervalSeconds[intervalType];
    const intervalsOdd = Number(span / secondsPerInterval) % 2 !== 0;

    const leftOverFromRange = span % secondsPerInterval;
    const secondsToAdd = secondsPerInterval - leftOverFromRange;

    if (intervalsOdd) {
      this.before += secondsToAdd;
    }
  }

  toIntlString(
    options: Intl.DateTimeFormatOptions = {},
    skipRangeLabel = false
  ): string {
    const optionsWithTimezone: Intl.DateTimeFormatOptions = {
      timeZone: getUserTimezone(),
      ...options,
    };

    return this.range && !skipRangeLabel
      ? this.range.localeLabel!
      : getLocaleText("date.pretty_ranges.range", {
          after: formatIntlDate(this.after, optionsWithTimezone),
          before: formatIntlDate(this.before, optionsWithTimezone),
        });
  }

  toString(format = "d MMM", options = { forceRange: false }): string {
    return this.range && !options.forceRange
      ? this.range.localeLabel!
      : getLocaleText("date.pretty_ranges.range", {
          after: this.afterAsString(format),
          before: this.beforeAsString(format),
        });
  }
}
